import { useEffect, useState } from "react";
import { Icon } from "./ui";
import { PhotoTile } from "./PhotoTile";
import { downloadPhoto, sharePhotos } from "../lib/download";

/** A lightbox slide — either a mock wash (seed) or a real photo/video (src). */
export interface LightboxTile {
  seed?: number;
  src?: string;
  video?: boolean;
}

interface LightboxProps {
  tiles: LightboxTile[];
  index: number;
  onClose: () => void;
}

export function Lightbox({ tiles, index, onClose }: LightboxProps) {
  const [i, setI] = useState(index);
  const [busy, setBusy] = useState(false);
  useEffect(() => setI(index), [index]);
  const go = (d: number) => setI((p) => (p + d + tiles.length) % tiles.length);
  const tile = tiles[i] ?? {};

  async function withBusy(fn: () => Promise<void>) {
    if (!tile.src || busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const download = () => withBusy(() => downloadPhoto(tile.src!).then(() => {}));
  const share = () => withBusy(() => sharePhotos([tile.src!]));

  return (
    <div className="lightbox" onClick={onClose}>
      <button className="lb-close" onClick={onClose} aria-label="Close">
        <Icon name="close" size={24} />
      </button>
      <div className="lb-stage" onClick={(e) => e.stopPropagation()}>
        <button className="lb-nav prev" onClick={() => go(-1)} aria-label="Previous">
          <Icon name="back" size={26} />
        </button>
        <PhotoTile seed={tile.seed} src={tile.src} video={tile.video} controls={tile.video} ratio="3 / 4" radius={8} className="lb-photo" />
        <button className="lb-nav next" onClick={() => go(1)} aria-label="Next">
          <Icon name="chevron" size={26} />
        </button>
      </div>
      <div className="lb-actions" onClick={(e) => e.stopPropagation()}>
        <button className="lb-act" onClick={download} disabled={!tile.src || busy} aria-label="Download">
          <Icon name="download" size={20} />
        </button>
        <button className="lb-act" onClick={share} disabled={!tile.src || busy} aria-label="Share">
          <Icon name="share" size={19} />
        </button>
      </div>
    </div>
  );
}
